import { ANGLE_STEPS, iatan2, withinRange } from '../math/fixed.ts';
import type { BattleState, CaveState, Enemy } from '../types.ts';
import { touchGrid } from '../grid.ts';
import { ev } from './combat.ts';

/**
 * The ice cave (docs/sendas/guerrero.md, chapter 4, act 2; the ground in battle/slide-layout.ts): three rooms one above
 * the other inside the blue ice, fallen sentinels (era IV) standing guard in each, facing the way in. From the front
 * nothing gets in (combat's `caveGuard`: a blow from within their guard's arc lands at `frontBp`); they wake when the
 * hero comes into their room and turn slowly (`turn` steps a tick at most), so the hero has to get round them. A room
 * cleared, the ice of its far door cracks open; past the third, Fermín and Juana, one against the other. Reaching them
 * wins it.
 *
 * Cost: a handful of sentinels, looked at once a tick; the doors are the grid's plugs, changed three times a fight.
 */

const fresh = (n: number): CaveState => ({ room: -1, ids: Array.from({ length: n }, () => []), awake: Array.from({ length: n }, () => false), cleared: Array.from({ length: n }, () => false), face: Array.from({ length: n }, () => []) });

/** The shortest turn from `a` to `b` (angle steps, −512 to 511). */
const turnOf = (a: number, b: number): number => ((((b - a) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS) - ANGLE_STEPS / 2;

/** The doors still shut (the rooms not yet cleared), as the grid's plugs. */
const plugs = (s: BattleState, st: CaveState): number[] => {
  const r = s.rules?.senda?.cave;
  if (!r) return [];
  return r.rooms
    .map((rm, i) => (st.cleared[i] ? -1 : rm.door))
    .filter((d) => d >= 0)
    .sort((a, b) => a - b);
};

export const caveTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.cave;
  const st0 = s.senda;
  if (!r || !st0) return;
  if (!st0.cave) {
    const st = fresh(r.rooms.length);
    st0.cave = st;
    s.plugs = plugs(s, st);
    touchGrid(s);
  }
  const st = st0.cave;
  if (!s.started) return;
  // Each sentinel to the room it stands in, as they come on (by the nearest room's middle).
  for (const e of s.enemies) {
    if (e.kind !== 'sentinel' || st.ids.some((l) => l.includes(e.id))) continue;
    let best = 0;
    let bd = -1;
    r.rooms.forEach((rm, i) => {
      const d = (rm.at.x - e.x) * (rm.at.x - e.x) + (rm.at.y - e.y) * (rm.at.y - e.y);
      if (bd < 0 || d < bd) {
        bd = d;
        best = i;
      }
    });
    // Facing the way in (down the cave: +y).
    e.facing = 256;
    (st.ids[best] as number[]).push(e.id);
    (st.face[best] as number[]).push(256);
  }
  // The hero's room: the sentinels in it wake.
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h)
    r.rooms.forEach((rm, i) => {
      // (Through the ice of a door still shut, a room next to the hero's sleeps on.)
      if (st.awake[i] || (i > 0 && !st.cleared[i - 1]) || !withinRange(h.x, h.y, rm.at.x, rm.at.y, rm.r)) return;
      st.awake[i] = true;
      st.room = i;
      ev(s, { t: 'senda', kind: 'wake', npc: '', x: rm.at.x, y: rm.at.y, n: i });
      cue(`room${i + 1}`);
    });
  // Their guard turns slowly: what the fight turned them to this tick, held back to `turn` steps from where they faced.
  st.ids.forEach((ids, i) => {
    const face = st.face[i] as number[];
    ids.forEach((id, k) => {
      const e = s.enemies.find((x) => x.id === id);
      if (!e || e.hp <= 0) return;
      const was = face[k] as number;
      const d = turnOf(was, e.facing);
      const now = d > r.turn ? was + r.turn : d < -r.turn ? was - r.turn : e.facing;
      const f = ((now % ANGLE_STEPS) + ANGLE_STEPS) % ANGLE_STEPS;
      face[k] = f;
      e.facing = f;
    });
    // All of a room's down: its door's ice cracks open.
    if (!st.cleared[i] && ids.length && ids.every((id) => !s.enemies.some((e) => e.id === id && e.hp > 0))) {
      st.cleared[i] = true;
      s.plugs = plugs(s, st);
      touchGrid(s);
      const rm = r.rooms[i];
      if (rm) ev(s, { t: 'senda', kind: 'open', npc: '', x: rm.at.x, y: rm.at.y, n: i });
      cue(`open${i + 1}`);
    }
  });
};

/** A sentinel asleep at its post: it stands, facing the way in (true: nothing else for it). */
export const caveHold = (s: BattleState, e: Enemy): boolean => {
  const st = s.senda?.cave;
  if (!s.rules?.senda?.cave || !st || e.kind !== 'sentinel') return false;
  const i = st.ids.findIndex((l) => l.includes(e.id));
  if (i < 0 || st.awake[i]) return false;
  e.anim = 'idle';
  return true;
};

/**
 * A blow on a sentinel in the cave (combat's damage): from within its guard's arc it lands at `frontBp` (nothing gets
 * in), from the side or behind, whole. `fromX`, `fromY`: where it came from.
 */
export const caveGuard = (s: BattleState, e: Enemy, dmg: number, fromX: number, fromY: number): number => {
  const r = s.rules?.senda?.cave;
  if (!r || e.kind !== 'sentinel') return dmg;
  const d = turnOf(e.facing, iatan2(fromY - e.y, fromX - e.x));
  if (d > r.arc || d < -r.arc) return dmg;
  if (s.tick % 6 === 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'blocked' });
  return Math.trunc((dmg * r.frontBp) / 10000);
};

/** Won within reach of Fermín and Juana (the hall's door open). */
export const caveResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.senda?.cave;
  const st = s.senda?.cave;
  if (!r || !st || !st.cleared.every(Boolean)) return null;
  return s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, r.hall.x, r.hall.y, r.reach)) ? 'won' : null;
};

/** How far along: rooms cleared of three. */
export const caveGoal = (s: BattleState): { done: number; need: number } => {
  const st = s.senda?.cave;
  const n = s.rules?.senda?.cave?.rooms.length ?? 1;
  return { done: st ? st.cleared.filter(Boolean).length : 0, need: n };
};
